import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Alert,
  RefreshControl,
  Platform,
  TouchableOpacity,
} from 'react-native';
import ModernButton from '../components/ModernButton';
import ModernCard from '../components/ModernCard';
import ModernLoading from '../components/ModernLoading';
import SuperAdminStripeConnectPanel from '../components/SuperAdminStripeConnectPanel';
import { getWebDb, initializeFirebase, UnifiedAuthService } from '../services/unifiedAuthService';
import { ESTIMATED_AVG_CREDIT_PRICE } from '../services/creditsService';
import {
  fetchAdminDashboardFromApi,
  fetchSalesRepsFromApi,
  isAdminApiConfigured,
  AdminBusinessData,
  AdminStats,
  AdminSalesRep,
} from '../services/adminApiService';

interface SuperAdminScreenProps {
  onLogout: () => void;
  onScanBusinessCard?: () => void;
}

interface BusinessData extends AdminBusinessData {}

interface AdminStatsView extends AdminStats {}

const SuperAdminScreen: React.FC<SuperAdminScreenProps> = ({ onLogout, onScanBusinessCard }) => {
  const [businesses, setBusinesses] = useState<BusinessData[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [adminTab, setAdminTab] = useState<'overview' | 'stripe_requests' | 'sales_reps'>('overview');
  const [salesReps, setSalesReps] = useState<AdminSalesRep[]>([]);
  const [salesRepsLoading, setSalesRepsLoading] = useState(false);
  const [salesRepsError, setSalesRepsError] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminStatsView>({
    totalUsers: 0,
    totalBusinesses: 0,
    activeBusinesses: 0,
    totalAppointments: 0,
    businessRevenue: 0,
    totalCreditsPurchasedRevenue: 0,
    businessSubscriptionRevenue: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const normalizeBusiness = (
    id: string,
    data: any,
    appointmentCount: number
  ): BusinessData => {
    const rawStatus = (data?.status || '').toLowerCase();
    const status: BusinessData['status'] =
      rawStatus === 'suspended'
        ? 'suspended'
        : data?.isActive === false || rawStatus === 'inactive'
          ? 'inactive'
          : 'active';

    const joinDate = data?.createdAt || new Date().toISOString();
    const appointments = appointmentCount || 0;
    const revenue = Number(data?.totalRevenue || 0);

    return {
      id,
      name: data?.name || 'Unnamed Business',
      industry: data?.category || 'General',
      status,
      plan: data?.plan || 'Basic',
      revenue: Number.isFinite(revenue) ? revenue : 0,
      appointments,
      joinDate: typeof joinDate === 'string' ? joinDate : new Date().toISOString(),
    };
  };

  const resolvePlanRevenue = (plan: string): number => {
    const key = (plan || '').trim().toLowerCase();
    const priceByPlan: Record<string, number> = {
      free: 0,
      basic: 29.99,
      starter: 29.99,
      professional: 79.99,
      pro: 79.99,
      enterprise: 149.99,
      business: 149.99,
      'founding-member-monthly': 39.99,
      'founding member monthly': 39.99,
      'founding-member-annual': 33.33,
      'founding member annual': 33.33,
      core: 29.99,
      premium: 79.99,
    };
    return priceByPlan[key] ?? 0;
  };

  const getAppointmentRevenueAmount = (appointment: any): number => {
    const fromPaymentData = Number(appointment?.paymentData?.amount);
    const fromAmount = Number(appointment?.amount);
    const fromEstimated = Number(appointment?.estimatedCost);
    const value = Number.isFinite(fromPaymentData) && fromPaymentData > 0
      ? fromPaymentData
      : Number.isFinite(fromAmount) && fromAmount > 0
        ? fromAmount
        : Number.isFinite(fromEstimated) && fromEstimated > 0
          ? fromEstimated
          : 0;
    return value;
  };

  const fetchDashboardDataFromFirestore = async (): Promise<{
    businesses: BusinessData[];
    stats: AdminStatsView;
  }> => {
    const emptyStats: AdminStatsView = {
      totalUsers: 0,
      totalBusinesses: 0,
      activeBusinesses: 0,
      totalAppointments: 0,
      businessRevenue: 0,
      totalCreditsPurchasedRevenue: 0,
      businessSubscriptionRevenue: 0,
    };

    const buildResult = (
      usersSnapshot: any,
      businessesSnapshot: any,
      appointmentsSnapshot: any,
      userCreditsSnapshot: any | null
    ) => {
      const appointmentCountsByBusiness = new Map<string, number>();
      const businessRevenueByBusiness = new Map<string, number>();
      appointmentsSnapshot.forEach((docSnap: any) => {
        const appointment = docSnap.data() || {};
        const businessOwnerId = appointment.businessOwnerId;
        if (!businessOwnerId) return;

        appointmentCountsByBusiness.set(
          businessOwnerId,
          (appointmentCountsByBusiness.get(businessOwnerId) || 0) + 1
        );
        businessRevenueByBusiness.set(
          businessOwnerId,
          (businessRevenueByBusiness.get(businessOwnerId) || 0) + getAppointmentRevenueAmount(appointment)
        );
      });

      const normalizedBusinesses = businessesSnapshot.docs.map((docSnap: any) =>
        normalizeBusiness(docSnap.id, docSnap.data(), appointmentCountsByBusiness.get(docSnap.id) || 0),
      );

      const businessesWithRevenue = normalizedBusinesses.map((b) => ({
        ...b,
        revenue: businessRevenueByBusiness.get(b.id) || b.revenue || 0,
      }));

      const totalCreditsPurchased = userCreditsSnapshot
        ? userCreditsSnapshot.docs.reduce((sum: number, docSnap: any) => {
            const data = docSnap.data() || {};
            const credits = Number(data.lifetimeCreditsPurchased || 0);
            return sum + (Number.isFinite(credits) ? credits : 0);
          }, 0)
        : 0;
      const creditsRevenue = totalCreditsPurchased * ESTIMATED_AVG_CREDIT_PRICE;

      const businessSubscriptionRevenue = businessesWithRevenue.reduce(
        (sum, b) => sum + resolvePlanRevenue(b.plan),
        0
      );

      return {
        businesses: businessesWithRevenue,
        stats: {
          totalUsers: usersSnapshot.size,
          totalBusinesses: businessesWithRevenue.length,
          activeBusinesses: businessesWithRevenue.filter((b) => b.status === 'active').length,
          totalAppointments: businessesWithRevenue.reduce((sum, b) => sum + b.appointments, 0),
          businessRevenue: businessesWithRevenue.reduce((sum, b) => sum + b.revenue, 0),
          totalCreditsPurchasedRevenue: creditsRevenue,
          businessSubscriptionRevenue,
        },
      };
    };

    try {
      if (Platform.OS === 'web') {
        initializeFirebase();
        const db = getWebDb();
        if (!db) {
          return { businesses: [], stats: emptyStats };
        }

        const { collection, getDocs } = await import('firebase/firestore');
        const [usersResult, businessesResult, appointmentsResult, userCreditsResult] =
          await Promise.allSettled([
            getDocs(collection(db, 'users')),
            getDocs(collection(db, 'businesses')),
            getDocs(collection(db, 'appointments')),
            getDocs(collection(db, 'userCredits')),
          ]);

        if (
          usersResult.status === 'rejected' ||
          businessesResult.status === 'rejected' ||
          appointmentsResult.status === 'rejected'
        ) {
          throw usersResult.status === 'rejected'
            ? usersResult.reason
            : businessesResult.status === 'rejected'
              ? businessesResult.reason
              : appointmentsResult.reason;
        }

        return buildResult(
          usersResult.value,
          businessesResult.value,
          appointmentsResult.value,
          userCreditsResult.status === 'fulfilled' ? userCreditsResult.value : null
        );
      }

      const { initializeFirebase: initMobileFirebase, getMobileFirestore } = await import(
        '../services/unifiedAuthService'
      );
      if (!initMobileFirebase()) {
        return { businesses: [], stats: emptyStats };
      }
      const mobileFirestore = getMobileFirestore?.();
      if (!mobileFirestore) {
        return { businesses: [], stats: emptyStats };
      }
      const db = mobileFirestore();

      const [usersResult, businessesResult, appointmentsResult, userCreditsResult] =
        await Promise.allSettled([
          db.collection('users').get(),
          db.collection('businesses').get(),
          db.collection('appointments').get(),
          db.collection('userCredits').get(),
        ]);

      if (
        usersResult.status === 'rejected' ||
        businessesResult.status === 'rejected' ||
        appointmentsResult.status === 'rejected'
      ) {
        throw usersResult.status === 'rejected'
          ? usersResult.reason
          : businessesResult.status === 'rejected'
            ? businessesResult.reason
            : appointmentsResult.reason;
      }

      return buildResult(
        usersResult.value,
        businessesResult.value,
        appointmentsResult.value,
        userCreditsResult.status === 'fulfilled' ? userCreditsResult.value : null
      );
    } catch (error) {
      console.error('Error fetching Firestore admin data:', error);
      return { businesses: [], stats: emptyStats };
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const currentUser = await UnifiedAuthService.getCurrentUser();
      const apiConfigured = await isAdminApiConfigured();

      if (apiConfigured) {
        const apiResult = await fetchAdminDashboardFromApi();
        if (apiResult.success && apiResult.stats) {
          setBusinesses(apiResult.businesses || []);
          setStats(apiResult.stats);
          return;
        }
        console.warn('Admin API dashboard failed, falling back to Firestore:', apiResult.error);
      } else if (!currentUser) {
        Alert.alert(
          'Sign in required',
          'Sign in with an approved admin account (via the super admin PIN screen) to load platform data.'
        );
      }

      const { businesses: firestoreBusinesses, stats: firestoreStats } =
        await fetchDashboardDataFromFirestore();
      const sortedBusinesses = firestoreBusinesses.sort(
        (a, b) => new Date(b.joinDate).getTime() - new Date(a.joinDate).getTime()
      );
      setBusinesses(sortedBusinesses);
      setStats(firestoreStats);

      if (firestoreStats.totalBusinesses === 0 && !apiConfigured) {
        Alert.alert(
          'No data loaded',
          currentUser
            ? 'No businesses were found in Firestore, or your account lacks permission to read admin collections.'
            : 'Sign in with an approved admin account to load platform data.'
        );
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to load admin dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const loadSalesReps = async () => {
    setSalesRepsLoading(true);
    setSalesRepsError(null);
    try {
      const result = await fetchSalesRepsFromApi();
      if (result.success) {
        setSalesReps(result.reps || []);
      } else {
        setSalesRepsError(result.error || 'Failed to load sales reps');
      }
    } catch (error: any) {
      setSalesRepsError(error?.message || 'Failed to load sales reps');
    } finally {
      setSalesRepsLoading(false);
    }
  };

  const handleBusinessAction = (businessId: string, action: string) => {
    Alert.alert(
      'Confirm Action',
      `Are you sure you want to ${action} this business?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          style: 'destructive',
          onPress: () => {
            // Handle action
            Alert.alert('Success', `Business ${action} successfully`);
          },
        },
      ]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#34C759';
      case 'inactive': return '#8E8E93';
      case 'suspended': return '#FF3B30';
      default: return '#8E8E93';
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'Basic': return '#8E8E93';
      case 'Professional': return '#007AFF';
      case 'Enterprise': return '#FF9500';
      default: return '#8E8E93';
    }
  };

  if (loading) {
    return <ModernLoading message="Loading admin dashboard..." />;
  }

  return (
    <ScrollView
      style={styles.container}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View>
            <Text style={styles.title}>Super Admin Dashboard</Text>
            <Text style={styles.subtitle}>Platform metrics and business analytics</Text>
          </View>
          {onScanBusinessCard ? (
            <ModernButton
              title="Scan Business Card"
              variant="secondary"
              size="small"
              onPress={onScanBusinessCard}
            />
          ) : null}
        </View>
        <View style={styles.tabRow}>
          <TouchableOpacity
            style={[styles.tab, adminTab === 'overview' && styles.tabActive]}
            onPress={() => setAdminTab('overview')}
          >
            <Text
              style={[styles.tabText, adminTab === 'overview' && styles.tabTextActive]}
            >
              Overview
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, adminTab === 'stripe_requests' && styles.tabActive]}
            onPress={() => setAdminTab('stripe_requests')}
          >
            <Text
              style={[
                styles.tabText,
                adminTab === 'stripe_requests' && styles.tabTextActive,
              ]}
            >
              Stripe requests
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, adminTab === 'sales_reps' && styles.tabActive]}
            onPress={() => {
              setAdminTab('sales_reps');
              if (salesReps.length === 0) loadSalesReps();
            }}
          >
            <Text
              style={[
                styles.tabText,
                adminTab === 'sales_reps' && styles.tabTextActive,
              ]}
            >
              Sales reps
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {adminTab === 'stripe_requests' ? (
        <SuperAdminStripeConnectPanel />
      ) : null}

      {adminTab === 'sales_reps' ? (
        <View style={styles.businessesContainer}>
          <View style={styles.headerContent}>
            <Text style={styles.sectionTitle}>Sales Reps</Text>
            <ModernButton title="Refresh" variant="outline" size="small" onPress={loadSalesReps} />
          </View>
          {salesRepsLoading ? (
            <ModernLoading message="Loading reps..." />
          ) : salesRepsError ? (
            <ModernCard style={styles.emptyCard}>
              <Text style={styles.emptyTitle}>Could not load reps</Text>
              <Text style={styles.emptySubtitle}>{salesRepsError}</Text>
            </ModernCard>
          ) : salesReps.length === 0 ? (
            <ModernCard style={styles.emptyCard}>
              <Text style={styles.emptyTitle}>No reps signed up yet</Text>
              <Text style={styles.emptySubtitle}>
                Share bookapt.io/sales-rep/signup to get started.
              </Text>
            </ModernCard>
          ) : (
            salesReps.map((rep) => (
              <ModernCard key={rep.code} style={styles.businessCard}>
                <View style={styles.businessHeader}>
                  <View>
                    <Text style={styles.businessName}>{rep.name}</Text>
                    <Text style={styles.businessIndustry}>{rep.city} · {rep.code}</Text>
                  </View>
                  <View style={styles.businessStatus}>
                    <View style={[styles.statusBadge, { backgroundColor: rep.active ? '#34C759' : '#8E8E93' }]}>
                      <Text style={styles.statusText}>{rep.active ? 'ACTIVE' : 'INACTIVE'}</Text>
                    </View>
                  </View>
                </View>
                <View style={styles.businessDetails}>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Businesses referred:</Text>
                    <Text style={styles.detailValue}>{rep.businessesReferred}</Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Revenue generated:</Text>
                    <Text style={styles.detailValue}>${(rep.totalRevenueCents / 100).toFixed(2)}</Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Commission earned:</Text>
                    <Text style={styles.detailValue}>${(rep.totalEarnedCents / 100).toFixed(2)}</Text>
                  </View>
                  {rep.pendingRequestCents > 0 ? (
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabel}>Pending payout request:</Text>
                      <Text style={[styles.detailValue, { color: '#FF9500' }]}>
                        ${(rep.pendingRequestCents / 100).toFixed(2)}
                      </Text>
                    </View>
                  ) : null}
                </View>
              </ModernCard>
            ))
          )}
        </View>
      ) : null}

      {adminTab === 'overview' ? (
        <>
      {/* Stats Cards */}
      <View style={styles.statsContainer}>
        <ModernCard style={styles.statCard}>
          <Text style={styles.statNumber}>{stats.totalUsers}</Text>
          <Text style={styles.statLabel}>Total Users</Text>
        </ModernCard>
        
        <ModernCard style={styles.statCard}>
          <Text style={[styles.statNumber, { color: '#34C759' }]}>
            {stats.totalBusinesses}
          </Text>
          <Text style={styles.statLabel}>Businesses Registered</Text>
        </ModernCard>
        
        <ModernCard style={styles.statCard}>
          <Text style={[styles.statNumber, { color: '#007AFF' }]}>
            {stats.totalAppointments}
          </Text>
          <Text style={styles.statLabel}>Appointments Booked</Text>
        </ModernCard>

        <ModernCard style={styles.statCard}>
          <Text style={[styles.statNumber, { color: '#FF9500' }]}>
            ${stats.businessRevenue.toLocaleString()}
          </Text>
          <Text style={styles.statLabel}>Business Revenue</Text>
        </ModernCard>

        <ModernCard style={styles.statCard}>
          <Text style={[styles.statNumber, { color: '#AF52DE' }]}>
            ${stats.totalCreditsPurchasedRevenue.toLocaleString()}
          </Text>
          <Text style={styles.statLabel}>Credits Revenue</Text>
        </ModernCard>

        <ModernCard style={styles.statCard}>
          <Text style={[styles.statNumber, { color: '#32ADE6' }]}>
            ${stats.businessSubscriptionRevenue.toLocaleString()}
          </Text>
          <Text style={styles.statLabel}>Subscription Revenue</Text>
        </ModernCard>

        <ModernCard style={styles.statCard}>
          <Text style={[styles.statNumber, { color: '#34C759' }]}>
            {stats.activeBusinesses}
          </Text>
          <Text style={styles.statLabel}>Active Businesses</Text>
        </ModernCard>
      </View>

      {/* Businesses List */}
      <View style={styles.businessesContainer}>
        <Text style={styles.sectionTitle}>Businesses</Text>

        {businesses.length === 0 ? (
          <ModernCard style={styles.emptyCard}>
            <Text style={styles.emptyTitle}>No businesses found</Text>
            <Text style={styles.emptySubtitle}>
              Add business owners in Firestore to see them here.
            </Text>
          </ModernCard>
        ) : businesses.map((business) => (
          <ModernCard key={business.id} style={styles.businessCard}>
            <View style={styles.businessHeader}>
              <View>
                <Text style={styles.businessName}>{business.name}</Text>
                <Text style={styles.businessIndustry}>{business.industry}</Text>
              </View>
              <View style={styles.businessStatus}>
                <View style={[
                  styles.statusBadge,
                  { backgroundColor: getStatusColor(business.status) }
                ]}>
                  <Text style={styles.statusText}>
                    {business.status.toUpperCase()}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.businessDetails}>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Plan:</Text>
                <Text style={[
                  styles.detailValue,
                  { color: getPlanColor(business.plan) }
                ]}>
                  {business.plan}
                </Text>
              </View>
              
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Revenue:</Text>
                <Text style={styles.detailValue}>
                  ${business.revenue.toLocaleString()}
                </Text>
              </View>
              
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Appointments:</Text>
                <Text style={styles.detailValue}>
                  {business.appointments}
                </Text>
              </View>
              
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Joined:</Text>
                <Text style={styles.detailValue}>
                  {new Date(business.joinDate).toLocaleDateString()}
                </Text>
              </View>
            </View>

            <View style={styles.businessActions}>
              <ModernButton
                title="View Details"
                variant="outline"
                size="small"
                onPress={() => {}}
                style={styles.actionButton}
              />
              <ModernButton
                title="Suspend"
                variant="warning"
                size="small"
                onPress={() => handleBusinessAction(business.id, 'suspend')}
                style={styles.actionButton}
              />
              <ModernButton
                title="Delete"
                variant="error"
                size="small"
                onPress={() => handleBusinessAction(business.id, 'delete')}
                style={styles.actionButton}
              />
            </View>
          </ModernCard>
        ))}
      </View>
        </>
      ) : null}

      <View style={styles.logoutContainer}>
        <ModernButton
          title="Logout"
          variant="error"
          onPress={onLogout}
        />
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    backgroundColor: '#1C1C1E',
    padding: 20,
    paddingTop: 60,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: 4,
  },
  tabRow: {
    flexDirection: 'row',
    marginTop: 16,
    gap: 8,
  },
  tab: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  tabActive: {
    backgroundColor: '#007AFF',
  },
  tabText: {
    color: 'rgba(255,255,255,0.85)',
    fontWeight: '600',
    fontSize: 14,
  },
  tabTextActive: {
    color: '#fff',
  },
  statsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 20,
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    padding: 16,
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#8E8E93',
    textAlign: 'center',
  },
  businessesContainer: {
    padding: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  businessCard: {
    marginBottom: 16,
  },
  emptyCard: {
    padding: 18,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 6,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#6C6C70',
  },
  businessHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  businessName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  businessIndustry: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 2,
  },
  businessStatus: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  businessDetails: {
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  detailLabel: {
    fontSize: 14,
    color: '#8E8E93',
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1C1C1E',
  },
  businessActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
  },
  logoutContainer: {
    paddingHorizontal: 20,
    paddingBottom: 30,
  },
});

export default SuperAdminScreen;
